import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { dayCity, isMultiCity, routeLabel } from "../lib/trip-route";
import { DraggableSheet, type SheetHandle } from "./discover-mobile-sheet";
import { TripMapBackdrop } from "./trip-planner/trip-map-backdrop";
import { TripTimelineView } from "./trip-planner/trip-timeline-view";
import { TripEmpty } from "./trip-planner/trip-empty";
import { CreateTripDialog } from "./trip-planner/create-trip-dialog";
import { AddStopDialog } from "./trip-planner/add-stop-dialog";
import { MobileTripList } from "./trip-planner/mobile-trip-list";
import { parseShareHash } from "./trip-planner/trip-export";
import { TripWhenDialog } from "./trip-planner/trip-when-dialog";
import { useStopOffers } from "../hooks/use-trip-booking";
import { tripActivitySlugs } from "../lib/trip-booking";
import { useTripActions, useTripContext } from "../state/trip-context";
import {
  useDiscoverActions,
  useDiscoverCities,
  useDiscoverItems,
} from "../state/discover-context";
import { formatNumber } from "../internal/format";
import type { DiscoverItem, Trip } from "../types";

import { Icon } from "@majourneys/icons";
const ALL_DAYS = "all";

function stopTotal(trip: Trip): number {
  return trip.days.reduce((n, d) => n + d.stops.length, 0);
}

/**
 * Read the id out of a `?t=<hash>` share link, during render.
 *
 * Not in an effect: the desktop TripPlanner is mounted on the phone too (its
 * shell is only `display: none`), and its own hydration effect strips the
 * parameter from the URL. Effects run after the whole tree has rendered, so an
 * effect here would be looking at a URL that had already been cleaned. Reading
 * it in a state initialiser gets there first.
 */
function readSharedTripId(): string | null {
  if (typeof window === "undefined") return null;
  const hash = new URLSearchParams(window.location.search).get("t");
  if (!hash) return null;
  return parseShareHash(hash)?.id ?? null;
}

/** The `?trip=` a link asked to open (the account's trip list sends one). */
export const OPEN_TRIP_PARAM = "trip";

export function readRequestedTripId(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(OPEN_TRIP_PARAM) || null;
}

/**
 * Keep `?trip=` in step with the open trip, so a refresh lands on the same
 * trip and closing it back to the list does not reopen it on refresh. The
 * rest of the query and the router's history state are left as they are.
 */
export function writeRequestedTripId(id: string | null): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if ((url.searchParams.get(OPEN_TRIP_PARAM) || null) === id) return;
  if (id) url.searchParams.set(OPEN_TRIP_PARAM, id);
  else url.searchParams.delete(OPEN_TRIP_PARAM);
  window.history.replaceState(window.history.state, "", url.toString());
}

/**
 * Mobile Trips: the set of trips, and one trip open over it.
 *
 * This used to be a single surface showing the active trip with a dropdown in
 * the sheet header to switch, so the other trips only existed if you remembered
 * they did. It opens on the set now (see MobileTripList) and a trip opens over
 * it — the map backdrop and the draggable sheet, whose header carries the day
 * tabs and whose body is the itinerary timeline, belong to the open trip.
 */
export function DiscoverMobileTrips() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { state } = useTripContext();
  const { setActiveTrip } = useTripActions();
  const items = useDiscoverItems();
  const cities = useDiscoverCities();
  const { selectItem } = useDiscoverActions();
  const [createOpen, setCreateOpen] = useState(false);
  const [dayTab, setDayTab] = useState<string>(ALL_DAYS);
  /** Which trip is open over the list. Null is the list itself. */
  const [openTripId, setOpenTripId] = useState<string | null>(null);

  const resolveItem = useMemo(() => {
    const map = new Map(items.map((item) => [item.id, item]));
    return (id: string): DiscoverItem | null => map.get(id) ?? null;
  }, [items]);

  const cityNameOf = useMemo(() => {
    const bySlug = new Map(cities.map((c) => [c.slug, c.name]));
    return (slug: string) => bySlug.get(slug) ?? slug;
  }, [cities]);

  // A link to one trip skips the list: a shared trip (`?t=`) once it has been
  // imported, or a trip picked from the account's list (`?trip=`). The trip
  // may arrive a moment after the page (storage hydration, the account's
  // trips being pulled), so this waits for it. Everything else opens on the set.
  const [linkedTripId] = useState<string | null>(
    () => readSharedTripId() ?? readRequestedTripId(),
  );
  const linkedOpened = useRef(false);
  useEffect(() => {
    if (linkedOpened.current || linkedTripId === null) return;
    if (!state.trips.some((tr) => tr.id === linkedTripId)) return;
    linkedOpened.current = true;
    setActiveTrip(linkedTripId);
    setDayTab(ALL_DAYS);
    setOpenTripId(linkedTripId);
  }, [linkedTripId, state.trips, setActiveTrip]);

  const openTrip = (id: string) => {
    setActiveTrip(id);
    setDayTab(ALL_DAYS);
    setOpenTripId(id);
    writeRequestedTripId(id);
  };

  const closeTrip = () => {
    setOpenTripId(null);
    writeRequestedTripId(null);
  };

  // Read back out of the store rather than held in state, so a rename shows
  // straight away and a trip deleted underneath us falls back to the list
  // rather than leaving an empty sheet over a map of nothing.
  const trip: Trip | null =
    openTripId === null
      ? null
      : state.trips.find((tr) => tr.id === openTripId) ?? null;

  return (
    <>
      {state.trips.length === 0 ? (
        <div className="mj-trip-mobile-empty">
          <TripEmpty onCreate={() => setCreateOpen(true)} onUseReady={openTrip} />
        </div>
      ) : trip === null ? (
        <MobileTripList
          trips={state.trips}
          resolveItem={resolveItem}
          cityNameOf={cityNameOf}
          lang={lang}
          onOpen={openTrip}
          onCreate={() => setCreateOpen(true)}
        />
      ) : (
        <MobileTripView
          trip={trip}
          dayTab={dayTab}
          onPickDay={setDayTab}
          onBack={closeTrip}
          resolveItem={resolveItem}
          onViewStop={(itemId) => selectItem(itemId)}
          lang={lang}
          t={t}
          rtl={i18n.dir(lang) === "rtl"}
        />
      )}
      <CreateTripDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
}

interface MobileTripViewProps {
  readonly trip: Trip;
  readonly dayTab: string;
  readonly onPickDay: (dayId: string) => void;
  readonly onBack: () => void;
  readonly resolveItem: (id: string) => DiscoverItem | null;
  readonly onViewStop: (itemId: string) => void;
  readonly lang: string;
  readonly t: ReturnType<typeof useTranslation>["t"];
  readonly rtl: boolean;
}

function MobileTripView({
  trip,
  dayTab,
  onPickDay,
  onBack,
  resolveItem,
  onViewStop,
  lang,
  t,
  rtl,
}: MobileTripViewProps) {
  const selectedDay =
    dayTab === ALL_DAYS ? null : trip.days.find((d) => d.id === dayTab) ?? null;
  const mapStops = selectedDay
    ? selectedDay.stops
    : trip.days.flatMap((d) => d.stops);

  // The map starts folded away behind a "Show map" pill: open, it covered
  // half the phone and pushed the day itself below the fold. Shown, it frames
  // the chosen day's stops (the day chips drive `mapStops`).
  const [mapShown, setMapShown] = useState(false);
  const sheetRef = useRef<SheetHandle | null>(null);
  const toggleMap = () => {
    const next = !mapShown;
    setMapShown(next);
    sheetRef.current?.snapTo(next ? "peek" : "full");
  };
  const [whenOpen, setWhenOpen] = useState(false);
  // The sheet rests at its middle snap when the map is shown, covering about
  // half the phone; the camera fits the stops into the half above it.
  const mapInset =
    typeof window === "undefined" ? 0 : Math.round(window.innerHeight * 0.52);

  const dayCount = trip.days.length;
  const stopCount = stopTotal(trip);

  const { removeStop, reorderStops, updateStopTime, updateStopNote } =
    useTripActions();
  const cities = useDiscoverCities();
  const [addStopDayId, setAddStopDayId] = useState<string | null>(null);

  // One request per activity across the whole trip, shared by every day.
  const slugs = useMemo(() => tripActivitySlugs(trip, resolveItem), [trip, resolveItem]);
  const offerFor = useStopOffers(slugs);

  // An empty map centres on where the shown day is: its own city on a trip
  // across several, the trip's otherwise.
  const centreCity = selectedDay ? dayCity(trip, selectedDay) : trip.city;
  const cityCoords = useMemo(() => {
    const c = cities.find((city) => city.slug === centreCity);
    if (!c || c.latitude == null || c.longitude == null) return null;
    return { lat: c.latitude, lon: c.longitude };
  }, [cities, centreCity]);

  const moveStop = (
    dayId: string,
    stopId: string,
    direction: "up" | "down",
  ) => {
    const day = trip.days.find((d) => d.id === dayId);
    if (!day) return;
    const ids = day.stops.map((s) => s.id);
    const from = ids.indexOf(stopId);
    const to = direction === "up" ? from - 1 : from + 1;
    if (from < 0 || to < 0 || to >= ids.length) return;
    [ids[from], ids[to]] = [ids[to]!, ids[from]!];
    reorderStops(trip.id, dayId, ids);
  };

  const saveNote = (
    dayId: string,
    stopId: string,
    note: string | undefined,
  ) => {
    updateStopNote(trip.id, dayId, stopId, note);
  };

  return (
    <>
      {mapShown ? (
        <TripMapBackdrop
          stops={mapStops}
          resolveItem={resolveItem}
          fallback={cityCoords}
          bottomInset={mapInset}
        />
      ) : (
        <div className="mj-trip-map-backdrop mj-trip-map-backdrop--empty" aria-hidden="true" />
      )}
      <DraggableSheet
        handleRef={sheetRef}
        initialSnap="full"
        // Dragged down, the sheet uncovers the map, so it shows itself:
        // there is nothing else to see behind the sheet. Back up to full, it
        // folds away again and the pill reads "Show map".
        onSnapChange={(next) => setMapShown(next !== "full")}
        header={
          <div className="mj-trip-sheet-head">
            <div className="mj-trip-sheet-head__top">
              {/* Back, not a dropdown: the list is a place you were, so the way
                  out of a trip is the way you came in. */}
              <div className="mj-trip-sheet-head__nav">
                <button
                  type="button"
                  data-no-drag
                  className="mj-trip-sheet-head__back"
                  onClick={onBack}
                  aria-label={t("trip.back_to_list")}
                >
                  <Icon
                    name={rtl ? "ri-arrow-right-line" : "ri-arrow-left-line"}
                    aria-hidden="true"
                  />
                </button>
                <h2 className="mj-trip-sheet-head__title">{trip.name}</h2>
                <button
                  type="button"
                  data-no-drag
                  className={`mj-trip-map-pill${mapShown ? " is-on" : ""}`}
                  aria-pressed={mapShown}
                  onClick={toggleMap}
                  title={mapShown ? t("trip.map.hide") : t("trip.map.show")}
                >
                  <Icon name="ri-map-2-line" aria-hidden="true" />
                  {/* Icon only on phones (owner, 2026-10-11): the words took
                      room the trip name needs. Screen readers still hear it. */}
                  <span className="mj-trip-map-pill__label">
                    {mapShown ? t("trip.map.hide") : t("trip.map.show")}
                  </span>
                </button>
              </div>
              <span className="mj-trip-sheet-head__meta">
                {/* The route only when there is one: a one-city trip says
                    its city on every day already. */}
                {isMultiCity(trip)
                  ? `${routeLabel(trip, (slug) => cities.find((c) => c.slug === slug)?.name ?? slug, rtl)} · `
                  : null}
                {t("trip.day_count", { count: dayCount })} ·{" "}
                {t("trip.timeline.spots", {
                  count: stopCount,
                  formattedCount: formatNumber(stopCount, lang),
                })}
              </span>
            </div>
            <div
              className="mj-trip-sheet-head__tabs"
              data-no-drag
              role="tablist"
              aria-label={t("trip.itinerary_view")}
            >
              <button
                type="button"
                data-no-drag
                role="tab"
                aria-selected={dayTab === ALL_DAYS}
                className={`mj-trip-day-tab${dayTab === ALL_DAYS ? " is-active" : ""}`}
                onClick={() => onPickDay(ALL_DAYS)}
              >
                {t("trip.timeline.all_days")}
              </button>
              {trip.days.map((day, i) => (
                <button
                  key={day.id}
                  type="button"
                  data-no-drag
                  role="tab"
                  aria-selected={dayTab === day.id}
                  className={`mj-trip-day-tab${dayTab === day.id ? " is-active" : ""}`}
                  onClick={() => onPickDay(day.id)}
                >
                  {t("trip.day_label", { number: i + 1 })}
                </button>
              ))}
            </div>
          </div>
        }
      >
        <div className="mj-trip-sheet-body">
          <TripTimelineView
            trip={trip}
            onlyDayId={selectedDay?.id ?? null}
            offerFor={offerFor}
            onChangeWhen={() => setWhenOpen(true)}
            resolveItem={resolveItem}
            onViewStop={onViewStop}
            editable
            onAddStop={(dayId) => setAddStopDayId(dayId)}
            onRemoveStop={(dayId, stopId) => removeStop(trip.id, dayId, stopId)}
            onMoveStop={moveStop}
            onUpdateStopTime={(dayId, stopId, start, end) =>
              updateStopTime(trip.id, dayId, stopId, start, end)
            }
            onEditStopNote={saveNote}
          />
        </div>
      </DraggableSheet>
      {addStopDayId && (
        <AddStopDialog
          trip={trip}
          dayId={addStopDayId}
          onClose={() => setAddStopDayId(null)}
        />
      )}
      {whenOpen && <TripWhenDialog trip={trip} onClose={() => setWhenOpen(false)} />}
    </>
  );
}
